import React, { useCallback, useState, useEffect, useRef } from 'react'
import { isNil } from 'lodash'

import useEndOfScroll from '../../hooks/useEndOfScroll'

import EpisodeList from './EpisodeList'
import ScrollFetch from '../../components/ScrollFetch'

import { ReleaseStatus } from '../../constants/enums'
import { useEpisodeListLazyQuery, Maybe, EpisodesOrderTypes, EpisodeListQueryVariables } from '../../types/generated'

export const OrderByReleaseStatusMap = {
  [ReleaseStatus.ONAIR]: EpisodesOrderTypes.NEWEST,
  [ReleaseStatus.UNKNOWN]: EpisodesOrderTypes.NEWEST,
  [ReleaseStatus.COMPLETED]: EpisodesOrderTypes.OLDEST,
  [ReleaseStatus.COMINGSOON]: EpisodesOrderTypes.NEWEST,
}

interface Props {
  programSlug: string
  // FIXME: Change type string to EpisodesOrderTypes when deployed GQ-179 after.
  episodeOrdering?: Maybe<EpisodesOrderTypes> | Maybe<string>
  releaseStatus?: ReleaseStatus
  isSeries: boolean
}

export default function EpisodeListContainer({ programSlug, episodeOrdering, releaseStatus, isSeries }: Props) {
  const [selectedOrderKey, setOrderKey] = useState<Maybe<EpisodesOrderTypes>>(() => {
    if (!isSeries) {
      return EpisodesOrderTypes.NEWEST
    }

    if (episodeOrdering) {
      return episodeOrdering as EpisodesOrderTypes
    }

    return isNil(releaseStatus) ? null : OrderByReleaseStatusMap[releaseStatus]
  })
  const [fetch, { data, called, loading, fetchMore }] = useEpisodeListLazyQuery({
    variables: {
      programSlug,
      /**
       * NOTE: Set fallback value EpisodeOrderTypes.NEWSET because ordering type won't accepted null.
       *       There not effect to fetch data because useEffect of line 79 that really firing fetch logic.
       */
      ordering: selectedOrderKey ?? EpisodesOrderTypes.NEWEST,
      page: 1,
    },
    notifyOnNetworkStatusChange: true,
  })
  const episodes = data?.episodes.nodes ?? []
  const isSelected = useRef(false)
  const variablesRef = useRef<Maybe<Omit<EpisodeListQueryVariables, 'page'>>>(null)
  const nextPageRef = useRef(data?.episodes?.pageInfo.nextPage)
  const episodeListRef = useRef<HTMLUListElement>(null)
  const fetchMoreRef = useRef(fetchMore)

  useEffect(() => {
    isSelected.current = false
    // eslint-disable-next-line
  }, [programSlug])

  useEffect(() => {
    if (isSelected.current) {
      return
    }

    if (episodeOrdering) {
      setOrderKey(episodeOrdering as EpisodesOrderTypes)
      return
    }

    if (releaseStatus) {
      setOrderKey(OrderByReleaseStatusMap[releaseStatus])
    }
    // eslint-disable-next-line
  }, [episodeOrdering, releaseStatus])

  useEffect(() => {
    if (!selectedOrderKey || !programSlug) {
      return
    }

    const variables = {
      programSlug,
      ordering: selectedOrderKey,
    }

    fetch({
      variables: {
        ...variables,
        page: 1,
      },
    })

    variablesRef.current = {
      ...variables,
    }
    // eslint-disable-next-line
  }, [selectedOrderKey, programSlug])

  const onOrderKeyChange = useCallback((key: EpisodesOrderTypes) => {
    isSelected.current = true
    setOrderKey(key)
  }, [])

  // NOTE: Logic for ScrollFetch
  useEffect(() => {
    // NOTE: fetchMore function is undefined if does not fetch ever.
    fetchMoreRef.current = fetchMore
  }, [fetchMore])

  useEffect(() => {
    nextPageRef.current = data?.episodes?.pageInfo.nextPage

    // eslint-disable-next-line
  }, [data?.episodes?.pageInfo.nextPage])

  useEndOfScroll(episodeListRef, () => {
    if (variablesRef.current && nextPageRef.current) {
      const variables = {
        ...variablesRef.current,
        page: nextPageRef.current,
      }

      fetchMoreRef
        .current?.({
          variables,
        })
        .then(() => {
          fetch({
            variables,
          })
        })
    }
  })

  return (
    <>
      <EpisodeList
        loading={loading}
        called={called}
        episodes={episodes}
        selectedOrderKey={selectedOrderKey}
        visibleOrder={isSeries}
        episodeListRef={episodeListRef}
        onOrderKeyChange={onOrderKeyChange}
      />
      {data?.episodes.pageInfo.hasNextPage && called && !loading && <ScrollFetch />}
    </>
  )
}
